/**
 * Color utility functions for theming
 */

/**
 * Get contrasting text color (black or white) based on background color
 * Uses luminance calculation to determine best readability
 *
 * @param hexColor - Background color in hex format (e.g., '#FF0000' or 'FF0000')
 * @returns '#000000' for light backgrounds, '#ffffff' for dark backgrounds
 *
 * @example
 * getContrastColor('#00FF00') // Returns '#000000' (black text on green)
 * getContrastColor('#000080') // Returns '#ffffff' (white text on navy)
 */
export function getContrastColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate relative luminance using sRGB coefficients
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light backgrounds (luminance > 0.5), white for dark
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Adjust color brightness by a percentage
 *
 * @param hexColor - Color in hex format
 * @param percent - Percentage to adjust (-100 to 100, negative = darker)
 * @returns Adjusted color in hex format
 *
 * @example
 * adjustColorBrightness('#808080', 20)  // Lighter gray
 * adjustColorBrightness('#808080', -20) // Darker gray
 */
export function adjustColorBrightness(hexColor: string, percent: number): string {
  const hex = hexColor.replace('#', '');

  let r = parseInt(hex.substr(0, 2), 16);
  let g = parseInt(hex.substr(2, 2), 16);
  let b = parseInt(hex.substr(4, 2), 16);

  r = Math.min(255, Math.max(0, r + (r * percent) / 100));
  g = Math.min(255, Math.max(0, g + (g * percent) / 100));
  b = Math.min(255, Math.max(0, b + (b * percent) / 100));

  return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
}

/**
 * Convert hex color to rgba with opacity
 *
 * @param hexColor - Color in hex format
 * @param opacity - Opacity value (0 to 1)
 * @returns Color in rgba format
 *
 * @example
 * hexToRgba('#FF0000', 0.5) // Returns 'rgba(255, 0, 0, 0.5)'
 */
export function hexToRgba(hexColor: string, opacity: number): string {
  const hex = hexColor.replace('#', '');

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Check if a color is considered "light" (luminance > 0.5)
 *
 * @param hexColor - Color in hex format
 * @returns true if the color is light, false if dark
 */
export function isLightColor(hexColor: string): boolean {
  const hex = hexColor.replace('#', '');

  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5;
}

/**
 * Get text color that works on a given background
 * Returns the appropriate color from a pair based on background
 *
 * @param backgroundColor - The background color
 * @param lightText - Color to use on dark backgrounds (default: white)
 * @param darkText - Color to use on light backgrounds (default: black)
 * @returns The appropriate text color
 *
 * @example
 * getTextColorForBackground('#000000') // Returns '#ffffff'
 * getTextColorForBackground('#FFFFFF') // Returns '#000000'
 * getTextColorForBackground('#FF0000', '#FFE0E0', '#800000') // Custom colors
 */
export function getTextColorForBackground(
  backgroundColor: string,
  lightText: string = '#ffffff',
  darkText: string = '#000000'
): string {
  return isLightColor(backgroundColor) ? darkText : lightText;
}
